import {
	Component,
	EventEmitter,
	Input,
	Output,
	OnChanges,
	SimpleChanges, ElementRef
} from '@angular/core';
import {
	animate,
	state,
	style,
	transition,
	trigger
} from '@angular/animations';
import { NavItems } from '../nav/nav.model';
import {ActivatedRoute, Router} from '@angular/router';
import { LeftNavMenuStyleModel } from '../left-nav-new/model/left-nav-menu-style.model';

export interface LeftNavItem extends NavItems {
	icon?: string;
	customIconClass?: string;
	title?: string;
	collapsible?: boolean;
	badge?: string;
	badgeIcon?: string;
	scrollable?: boolean;
	index?: number;
}

@Component({
	selector: 'cv-left-nav',
	templateUrl: './left-nav.component.html',
	styleUrls: ['./left-nav.component.scss'],
	animations: [
		trigger('collapse', [
			state('isOpen', style({ width: '{{width}}px' }), { params: { width: 210 } }),
			state('isClosed', style({ width: '50px' })),
			state('default', style({ transform: 'rotate(0)' })),
			state('rotated', style({ transform: 'rotate(-180deg)' })),
			transition('isOpen <=> isClosed', animate('200ms')),
			transition('rotated => default', animate('300ms ease-out')),
			transition('default => rotated', animate('300ms ease-in'))
		]),
		trigger('collapse-up', [
			state('opened', style({ display: 'contents' })),
			state('closed', style({ display: 'none' })),
			state('default', style({ transform: 'rotate(0)' })),
			state('rotated', style({ transform: 'rotate(-180deg)' })),
			transition('opened <=> closed', animate('200ms')),
			transition('rotated => default', animate('300ms ease-out')),
			transition('default => rotated', animate('300ms ease-in'))
		])
	]
})
/* eslint-disable @angular-eslint/no-output-on-prefix */
export class LeftNavComponent implements OnChanges {
	@Output() onExpand: EventEmitter<any> = new EventEmitter<any>();
	@Output() onCollapse: EventEmitter<any> = new EventEmitter<any>();
	@Output() onRouteChange: EventEmitter<any> = new EventEmitter<any>();
	@Input() items: Array<LeftNavItem>;
	@Input() width: number;
	@Input() uiConfigStyle: LeftNavMenuStyleModel = new LeftNavMenuStyleModel();
	toggleState: string;
	navItems: Array<any>;
	collapsedStates: Map<number, string>;

	constructor(
		public route: ActivatedRoute,
		public router: Router,
		private elementRef: ElementRef
	) {
		this.isOpen = true;
		this.width = 210;
		this.toggleState = 'isOpen';
		this.collapsedStates = new Map<number, string>();
	}

	_isOpen: boolean;
	_selectedMenuItem: LeftNavItem = null;

	get selectedMenuItem(): any {
		return this._selectedMenuItem;
	}

	set selectedMenuItem(item: any) {
		this._selectedMenuItem = item;
	}

	@Input()
	get isOpen(): boolean {
		return this._isOpen;
	}
	set isOpen(value: boolean) {
		this._isOpen = value;
		this.toggleState = this._isOpen ? 'isOpen' : 'isClosed';
	}

	/**
	 * On Toggle switch click, collapse or expand component.
	 * It emits the corresponding expand/collapse events as well.
	 */
	onToggle(): void {
		this.isOpen = !this.isOpen;
		if (this.isOpen) {
			this.onExpand.emit();
		} else {
			this.onCollapse.emit();
		}
	}

	/**
	 * On Toggle switch click, collapse or expand component.
	 * It emits the corresponding expand/collapse events as well.
	 */
	onToggleCollapse(index: number): void {
		if (this.collapsedStates.has(index)) {
			const newState = (this.collapsedStates.get(index) === 'opened' ? 'closed' : 'opened');
			this.collapsedStates.set(index, newState);
		} else {
			this.collapsedStates.set(index, 'closed');
		}
	}

	/**
	 * Is the component collapsed ?
	 */
	isParentCollapsed(index: number): boolean {
		return this.collapsedStates.get(index) === 'closed';
	}

	/**
	 * Returns toggle icon state based on the component state.
	 */
	getCollapsedIconState(index: number): string {
		return this.collapsedStates.has(index) && this.collapsedStates.get(index) === 'closed' ? 'rotated' : 'default';
	}

	/**
	 * Is the component collapsed ?
	 */
	isCollapsed(): boolean {
		return this.toggleState === 'isClosed';
	}

	/**
	 * Returns toggle icon state based on the component state.
	 */
	getToggleIconState(): string {
		return this.isOpen ? 'default' : 'rotated';
	}

	/**
	 * Event triggers every time the route changes and sends the item:LeftNavItem that got selected.
	 * @param item: LeftNavItem
	 */
	onRouteClick($event: MouseEvent, item: LeftNavItem): void {
		if (!this.canNavigate(item)) {
			// Emit and Keep the last selected element
			this.selectedMenuItem = item;
			this.onRouteChange.emit(item);
		}
	}

	/**
	 * Return the correct icon class to apply based on item properties.
	 * @param item: LeftNavItem
	 */
	getIconClass(item: LeftNavItem): string {
		if (item.customIconClass) {
			return item.customIconClass;
		} else {
			return `fa ${ item.icon }`;
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		this.setItemsNav();

		for (const propName in changes) {
			if (changes[propName]) {
				switch (propName) {
					case 'uiConfigStyle': {
						this.compileStyle();
						break;
					}
				}
			}
		}
	}

	/**
	 * For the given element compile a new set of colors
	 * Some are possible to be changed by ngStyle, but others like :hover, :focus only exists as seudo-classes
	 * this ensure that those extra classes are properly compiled on the Component
	 */
	public compileStyle(): void {
		if (this.uiConfigStyle !== null) {
			const style: string = `
				.cv-left-nav__container-content { border-bottom: solid 1px ${this.uiConfigStyle.item?.separatorColor} !important; }
				.cv-left-nav__container__nav-item__nav-link:hover { background: ${this.uiConfigStyle.item?.active?.backgroundColor} !important; }
				.cv-left-nav__container__nav-item__nav-link.action-select.active { background: ${this.uiConfigStyle.item?.active?.backgroundColor} !important; }
				.cv-left-nav__container__nav-item__nav-link.action-select .cv-left-nav__container__nav-item__nav-link__nav-icon { color: ${this.uiConfigStyle.item?.textColor} !important; }
				.cv-left-nav__container__nav-item__nav-link.action-select.active .cv-left-nav__container__nav-item__nav-link__nav-icon { color: ${this.uiConfigStyle.item?.active?.iconColor} !important; }
				.cv-left-nav__container__nav-item__badge.item-badge { color: ${this.uiConfigStyle?.item?.active?.textColor} !important; } 
			`;
			const styleElement = document.createElement('style');
			styleElement.appendChild(document.createTextNode(style));
			this.elementRef.nativeElement.appendChild(styleElement);
		}
	}

	/**
	 * If enable events flag is on, then user can't route navigate.
	 */
	canNavigate(item: LeftNavItem): boolean {
		if (this.selectedMenuItem !== null && !this.selectedMenuItem.scrollable) {
			// It means we are on none scrollable element and we want to go to probably an scrollable item
			return true;
		}

		return item.scrollable ? false : true;
	}

	/**
	 * Pass when Angular detects the item is the current route selected
	 * so we can save latest element selected
	 * @param item
	 */
	public onRouterLinkActive(item: LeftNavItem): boolean {
		if (!item.scrollable && this.router.url.includes(item.route)) {
			this.selectedMenuItem = item;
			return true;
		}
		return false;
	}

	/**
	 * Sets the menu item as active (search by id or route). All others are set to inactive.
	 * @param item: LeftNavItem
	 */
	public setItemAsActive(item: LeftNavItem): void {
		let match = null;
		// Search by ID or route
		if (item.id) {
			match = this.items.find(menu => menu.id === item.id);
		} else {
			match = this.items.find(menu => menu.route === item.route);
		}
		if (match) {
			this.items.forEach(menu => {
				menu.active = false;
			});
			// Mark as active and we keep the last selected element
			match.active = true;
			this.selectedMenuItem = match;

			this.setItemsNav();
		}
	}

	/**
	 * set Items nav whether has title or not
	 */

	private setItemsNav(): void {
		this.navItems = [];
		if (this.items) {
			const titlesNav = new Set(this.items.map(item => item.title));
			if (titlesNav.size) {
				titlesNav.forEach(title =>
					this.navItems.push({
						title: title,
						collapsible: this.items.filter(i => i.title === title && i.collapsible).length ? true : false,
						items: [...this.items.filter(i => i.title === title)]
					})
				);
			}
		}
	}

	/**
	 * Test
	 * $('.cv-left-nav-component ul.cv-left-nav__container-content') // All grouped menus
	 * 
	 * THERE IS ONLY ONE li.cv-left-nav__container__nav-item-title PER ul.cv-left-nav__container-content
	 * // Title of the Group
	 * $('.cv-left-nav-component ul.cv-left-nav__container-content:nth-child({index}) li.cv-left-nav__container__nav-item-title .item-groupValue').text()
	 * // Badge value of the Group
	 * $('.cv-left-nav-component ul.cv-left-nav__container-content:nth-child({index}) li.cv-left-nav__container__nav-item-title .item-groupBadge').text()
	 * // Toggle item list (if collpasible)
	 * $('.cv-left-nav-component ul.cv-left-nav__container-content:nth-child({index}) li.cv-left-nav__container__nav-item-title .action-toggle').click()
	 * 
	 * // List of menu items for certain group
	 * $('.cv-left-nav-component ul.cv-left-nav__container-content:nth-child({index}) li.nav-item')
	 * // Select one menu item
	 * $('.cv-left-nav-component ul.cv-left-nav__container-content:nth-child({index}) li.nav-item .action-select')[{item_index}].click() 
	 * // Item value (name)
	 * $('.cv-left-nav-component ul.cv-left-nav__container-content:nth-child({index}) li.nav-item .item-value')[{item_index}].text() 
	 * // Item badge value (if exists)
	 * $('.cv-left-nav-component ul.cv-left-nav__container-content:nth-child({index}) li.nav-item .item-badge')[{item_index}].text() 
	 */
}
